import { supabase } from '../lib/supabase';

export const approvalWorkflowService = {
  // ============================================================================
  // APPROVAL CHAIN TEMPLATES
  // ============================================================================

  async getAllChainTemplates() {
    const { data, error } = await supabase
      ?.from('approval_chain_templates')
      ?.select('*')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      templateName: row?.template_name,
      entityType: row?.entity_type,
      description: row?.description,
      isDefault: row?.is_default,
      chainStatus: row?.chain_status,
      createdBy: row?.created_by,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getChainTemplatesByEntityType(entityType) {
    const { data, error } = await supabase
      ?.from('approval_chain_templates')
      ?.select('*')
      ?.eq('entity_type', entityType)
      ?.eq('chain_status', 'active');
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      templateName: row?.template_name,
      entityType: row?.entity_type,
      description: row?.description,
      isDefault: row?.is_default,
      createdAt: row?.created_at
    })) || [];
  },

  async getChainTemplateWithSteps(templateId) {
    const { data, error } = await supabase
      ?.from('approval_chain_templates')
      ?.select(`
        *,
        approval_steps (*),
        approval_routing_rules (*),
        escalation_rules (*)
      `)
      ?.eq('id', templateId)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      templateName: data?.template_name,
      entityType: data?.entity_type,
      description: data?.description,
      isDefault: data?.is_default,
      chainStatus: data?.chain_status,
      steps: data?.approval_steps?.map(step => ({
        id: step?.id,
        stepOrder: step?.step_order,
        stepName: step?.step_name,
        approverRole: step?.approver_role,
        approverEmail: step?.approver_email,
        isRequired: step?.is_required,
        canDelegate: step?.can_delegate,
        timeoutHours: step?.timeout_hours
      })) || [],
      routingRules: data?.approval_routing_rules?.map(rule => ({
        id: rule?.id,
        ruleName: rule?.rule_name,
        conditionType: rule?.condition_type,
        conditionValue: rule?.condition_value,
        priority: rule?.priority,
        isActive: rule?.is_active
      })) || [],
      escalationRules: data?.escalation_rules?.map(rule => ({
        id: rule?.id,
        triggerType: rule?.trigger_type,
        triggerCondition: rule?.trigger_condition,
        escalateToRole: rule?.escalate_to_role,
        escalateToEmail: rule?.escalate_to_email,
        notificationMessage: rule?.notification_message,
        isActive: rule?.is_active
      })) || []
    };
  },

  // ============================================================================
  // APPROVAL INSTANCES
  // ============================================================================

  async initiateApprovalWorkflow(entityType, entityId, entityMetadata, initiatedBy) {
    const { data, error } = await supabase
      ?.rpc('initiate_approval_workflow', {
        p_entity_type: entityType,
        p_entity_id: entityId,
        p_entity_metadata: entityMetadata,
        p_initiated_by: initiatedBy
      });
    
    if (error) throw error;
    
    return data;
  },

  async getAllApprovalInstances() {
    const { data, error } = await supabase
      ?.from('approval_instances')
      ?.select(`
        *,
        approval_chain_templates (template_name, entity_type)
      `)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      chainTemplateId: row?.chain_template_id,
      templateName: row?.approval_chain_templates?.template_name,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      entityMetadata: row?.entity_metadata,
      currentStepOrder: row?.current_step_order,
      overallStatus: row?.overall_status,
      initiatedBy: row?.initiated_by,
      initiatedAt: row?.initiated_at,
      completedAt: row?.completed_at,
      createdAt: row?.created_at
    })) || [];
  },

  async getApprovalInstancesByEntity(entityType, entityId) {
    const { data, error } = await supabase
      ?.from('approval_instances')
      ?.select(`
        *,
        approval_step_executions (*)
      `)
      ?.eq('entity_type', entityType)
      ?.eq('entity_id', entityId)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      entityType: row?.entity_type,
      entityId: row?.entity_id,
      currentStepOrder: row?.current_step_order,
      overallStatus: row?.overall_status,
      initiatedBy: row?.initiated_by,
      initiatedAt: row?.initiated_at,
      completedAt: row?.completed_at,
      steps: row?.approval_step_executions?.map(step => ({
        id: step?.id,
        stepOrder: step?.step_order,
        stepName: step?.step_name,
        assignedToRole: step?.assigned_to_role,
        assignedToEmail: step?.assigned_to_email,
        stepStatus: step?.step_status,
        approvedBy: step?.approved_by,
        approvedAt: step?.approved_at,
        rejectionReason: step?.rejection_reason,
        comments: step?.comments
      })) || []
    })) || [];
  },

  async getPendingApprovals() {
    const { data, error } = await supabase
      ?.from('approval_step_executions')
      ?.select(`
        *,
        approval_instances (
          entity_type,
          entity_id,
          entity_metadata,
          initiated_by,
          initiated_at
        )
      `)
      ?.eq('step_status', 'pending')
      ?.order('created_at', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      approvalInstanceId: row?.approval_instance_id,
      stepOrder: row?.step_order,
      stepName: row?.step_name,
      assignedToRole: row?.assigned_to_role,
      assignedToEmail: row?.assigned_to_email,
      stepStatus: row?.step_status,
      timeoutAt: row?.timeout_at,
      entityType: row?.approval_instances?.entity_type,
      entityId: row?.approval_instances?.entity_id,
      entityMetadata: row?.approval_instances?.entity_metadata,
      initiatedBy: row?.approval_instances?.initiated_by,
      initiatedAt: row?.approval_instances?.initiated_at,
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // APPROVAL ACTIONS
  // ============================================================================

  async approveStep(stepExecutionId, approvedBy, comments) {
    const { data, error } = await supabase
      ?.from('approval_step_executions')
      ?.update({
        step_status: 'approved',
        approved_by: approvedBy,
        approved_at: new Date()?.toISOString(),
        comments: comments
      })
      ?.eq('id', stepExecutionId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    // Check if this was the last step and update instance status
    const { data: instance } = await supabase
      ?.from('approval_instances')
      ?.select('id, current_step_order')
      ?.eq('id', data?.approval_instance_id)
      ?.single();
    
    const { data: totalSteps } = await supabase
      ?.from('approval_step_executions')
      ?.select('id', { count: 'exact' })
      ?.eq('approval_instance_id', data?.approval_instance_id);
    
    if (instance && data?.step_order === totalSteps?.length) {
      await supabase
        ?.from('approval_instances')
        ?.update({
          overall_status: 'approved',
          completed_at: new Date()?.toISOString()
        })
        ?.eq('id', data?.approval_instance_id);
    } else {
      await supabase
        ?.from('approval_instances')
        ?.update({
          current_step_order: data?.step_order + 1
        })
        ?.eq('id', data?.approval_instance_id);
    }
    
    return {
      id: data?.id,
      stepStatus: data?.step_status,
      approvedBy: data?.approved_by,
      approvedAt: data?.approved_at
    };
  },

  async rejectStep(stepExecutionId, rejectedBy, rejectionReason) {
    const { data, error } = await supabase
      ?.from('approval_step_executions')
      ?.update({
        step_status: 'rejected',
        approved_by: rejectedBy,
        approved_at: new Date()?.toISOString(),
        rejection_reason: rejectionReason
      })
      ?.eq('id', stepExecutionId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    // Update instance status to rejected
    await supabase
      ?.from('approval_instances')
      ?.update({
        overall_status: 'rejected',
        completed_at: new Date()?.toISOString()
      })
      ?.eq('id', data?.approval_instance_id);
    
    return {
      id: data?.id,
      stepStatus: data?.step_status,
      rejectionReason: data?.rejection_reason
    };
  },

  // ============================================================================
  // DELEGATION
  // ============================================================================

  async delegateApproval(stepExecutionId, delegatedFromEmail, delegatedToEmail, delegationReason, validUntil) {
    const { data: stepData, error: stepError } = await supabase
      ?.from('approval_step_executions')
      ?.update({
        step_status: 'delegated',
        assigned_to_email: delegatedToEmail
      })
      ?.eq('id', stepExecutionId)
      ?.select()
      ?.single();
    
    if (stepError) throw stepError;
    
    const { data, error } = await supabase
      ?.from('approval_delegations')
      ?.insert({
        step_execution_id: stepExecutionId,
        delegated_from_email: delegatedFromEmail,
        delegated_to_email: delegatedToEmail,
        delegation_reason: delegationReason,
        valid_until: validUntil
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      stepExecutionId: data?.step_execution_id,
      delegatedFromEmail: data?.delegated_from_email,
      delegatedToEmail: data?.delegated_to_email,
      delegationStatus: data?.delegation_status,
      validUntil: data?.valid_until
    };
  },

  async revokeDelegation(delegationId) {
    const { data, error } = await supabase
      ?.from('approval_delegations')
      ?.update({
        delegation_status: 'revoked',
        revoked_at: new Date()?.toISOString()
      })
      ?.eq('id', delegationId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      delegationStatus: data?.delegation_status,
      revokedAt: data?.revoked_at
    };
  },

  async getActiveDelegations() {
    const { data, error } = await supabase
      ?.from('approval_delegations')
      ?.select(`
        *,
        approval_step_executions (
          step_name,
          approval_instances (
            entity_type,
            entity_id
          )
        )
      `)
      ?.eq('delegation_status', 'active')
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      stepExecutionId: row?.step_execution_id,
      stepName: row?.approval_step_executions?.step_name,
      entityType: row?.approval_step_executions?.approval_instances?.entity_type,
      delegatedFromEmail: row?.delegated_from_email,
      delegatedToEmail: row?.delegated_to_email,
      delegationReason: row?.delegation_reason,
      validUntil: row?.valid_until,
      delegatedAt: row?.delegated_at
    })) || [];
  },

  // ============================================================================
  // ESCALATIONS
  // ============================================================================

  async getEscalations() {
    const { data, error } = await supabase
      ?.from('approval_escalations')
      ?.select(`
        *,
        approval_step_executions (
          step_name,
          approval_instances (
            entity_type,
            entity_id
          )
        )
      `)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      stepExecutionId: row?.step_execution_id,
      stepName: row?.approval_step_executions?.step_name,
      entityType: row?.approval_step_executions?.approval_instances?.entity_type,
      triggerType: row?.trigger_type,
      escalatedFromEmail: row?.escalated_from_email,
      escalatedToEmail: row?.escalated_to_email,
      escalationReason: row?.escalation_reason,
      escalatedAt: row?.escalated_at,
      resolvedAt: row?.resolved_at
    })) || [];
  },

  async resolveEscalation(escalationId) {
    const { data, error } = await supabase
      ?.from('approval_escalations')
      ?.update({
        resolved_at: new Date()?.toISOString()
      })
      ?.eq('id', escalationId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      resolvedAt: data?.resolved_at
    };
  },

  // ============================================================================
  // APPROVAL HISTORY
  // ============================================================================

  async getApprovalHistory(approvalInstanceId) {
    const { data, error } = await supabase
      ?.from('approval_history')
      ?.select('*')
      ?.eq('approval_instance_id', approvalInstanceId)
      ?.order('created_at', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      actionType: row?.action_type,
      actionDescription: row?.action_description,
      performedBy: row?.performed_by,
      stepOrder: row?.step_order,
      previousStatus: row?.previous_status,
      newStatus: row?.new_status,
      metadata: row?.metadata,
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // ROUTING
  // ============================================================================

  async determineApprovalChain(entityType, entityMetadata) {
    const { data, error } = await supabase
      ?.rpc('determine_approval_chain', {
        p_entity_type: entityType,
        p_entity_metadata: entityMetadata
      });
    
    if (error) throw error;
    
    return data;
  }
};

export default approvalWorkflowService;